import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isDevToolsBlocked } from "../../../config/apiconfig";
import "./DevToolsGuard.scss";

/** Docked DevTools only — ignore normal chrome/scrollbar gaps (avoids false positives). */
const THRESHOLD_PX = 220;

function isDevToolsLikelyOpen() {
  const widthGap = window.outerWidth - window.innerWidth;
  const heightGap = window.outerHeight - window.innerHeight;
  // Only large positive gaps (devtools docked to side/bottom). Negatives = OS UI.
  return widthGap > THRESHOLD_PX || heightGap > THRESHOLD_PX;
}

function isBlockedShortcut(event) {
  const key = String(event.key || "").toLowerCase();
  if (key === "f12") return true;

  const ctrlOrMeta = event.ctrlKey || event.metaKey;
  if (!ctrlOrMeta) return false;

  if (event.shiftKey && (key === "i" || key === "j" || key === "c" || key === "k")) {
    return true;
  }
  if (key === "u") return true;
  return false;
}

/**
 * Soft DevTools guard — enabled via apiconfig.js `blockDevTools` per environment.
 * Shows an organization-style dialog when DevTools shortcuts / docked tools are used.
 */
export default function DevToolsGuard() {
  const enabled = isDevToolsBlocked();
  const [blocked, setBlocked] = useState(false);
  const openRef = useRef(false);

  const showBlock = useCallback(() => {
    if (openRef.current) return;
    openRef.current = true;
    setBlocked(true);
  }, []);

  const dismiss = useCallback(() => {
    openRef.current = false;
    setBlocked(false);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      if (!isBlockedShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
      showBlock();
    };

    const onContextMenu = (event) => {
      event.preventDefault();
      showBlock();
    };

    const poll = () => {
      if (isDevToolsLikelyOpen()) {
        showBlock();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    const timerId = window.setInterval(poll, 1200);
    poll();

    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
      window.clearInterval(timerId);
    };
  }, [enabled, showBlock]);

  if (!enabled || !blocked) return null;

  return createPortal(
    <div
      className="devtools-guard"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="devtools-guard-title"
      aria-describedby="devtools-guard-desc"
    >
      <div className="devtools-guard__dialog">
        <h2 id="devtools-guard-title" className="devtools-guard__title">
          DevTools not allowed on this page
        </h2>
        <p id="devtools-guard-desc" className="devtools-guard__message">
          <i className="fas fa-building" aria-hidden="true" />
          <span>
            Your organization has blocked the use of DevTools on this page
          </span>
        </p>
        <div className="devtools-guard__actions">
          <button
            type="button"
            className="devtools-guard__ok"
            onClick={dismiss}
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
